import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Info from '@/components/Info'
import InlineLink from '@/components/InlineLink'
import useLayout from '@/layout/useLayout'
import { useAppQuery } from '@/lib/queries'

import useCheckout from '../../hooks/useCheckout'
import Step from '../Step/Step'
import TaxIdModal from './_partials/TaxIdModal'
import {
  PaymentInfo,
  PaymentMethodOptions,
  PaymentMethodTitle,
  PaymentMethodWrapper,
  PaymentOptionContent,
  PaymentOptionIcons,
  PaymentOptionWrapper,
  PaymentTaxIdButtons,
  PaymentTaxIdWrapper
} from './PaymentMethod.styles'

interface PaymentOptionProps {
  children: React.ReactNode
  icons?: string[]
  active: boolean
  vertical?: boolean
  onClick: () => void
}

const PaymentOption = ({ children, icons, active, vertical, onClick }: PaymentOptionProps) => {
  return (
    <PaymentOptionWrapper active={active} vertical={vertical} onClick={onClick}>
      <PaymentOptionContent>
        <h4>{children}</h4>
      </PaymentOptionContent>
      <PaymentOptionIcons>{icons?.map((icon, index) => <img key={index} src={icon} alt="..." />)}</PaymentOptionIcons>
    </PaymentOptionWrapper>
  )
}

const PaymentTaxId = () => {
  const { t } = useTranslation()
  const { taxId, setTaxId } = useCheckout()
  const [showTaxIdModal, setShowTaxIdModal] = useState(false)

  return (
    <PaymentTaxIdWrapper>
      {!taxId && <InlineLink onClick={() => setShowTaxIdModal(true)}>{t('addTaxId')}</InlineLink>}
      {taxId && (
        <>
          <p>{t('taxIdAdded')}</p>
          <strong>{taxId}</strong>
          <PaymentTaxIdButtons>
            <InlineLink onClick={() => setShowTaxIdModal(true)}>{t('edit')}</InlineLink>
            <InlineLink variant="danger" onClick={() => setTaxId('')}>
              {t('remove')}
            </InlineLink>
          </PaymentTaxIdButtons>
        </>
      )}
      {showTaxIdModal && <TaxIdModal onClose={() => setShowTaxIdModal(false)} />}
    </PaymentTaxIdWrapper>
  )
}

const Payment = () => {
  const { t } = useTranslation()
  const { paymentMethod, setPaymentMethod } = useCheckout()
  const { data: appData } = useAppQuery()
  // @ts-expect-error
  const { externalDevices } = appData || {}
  const { accessibilityMode } = useLayout()

  const TERMINAL_TYPES = ['paymentTerminal', 'eServiceTerminal', 'stripeTerminal']
  const hasPaymentTerminal = externalDevices?.some((device: { type: string }) => TERMINAL_TYPES.includes(device.type)) ?? false

  const methods = [
    {
      name: 'creditCard',
      title: t('paymentCard'),
      icons: ['/img/payment/visa.png', '/img/payment/mastercard.png', '/img/payment/amex.png'],
      show: hasPaymentTerminal
    }
  ]

  const computedMethods = methods.filter(method => method.show)

  return (
    <Step padded={true}>
      <PaymentMethodWrapper>
        <PaymentMethodTitle>{t('selectPaymentMethod')}</PaymentMethodTitle>
        <PaymentMethodOptions>
          {computedMethods.map(({ icons, name, title }) => (
            <PaymentOption
              vertical={accessibilityMode}
              key={name}
              onClick={() => setPaymentMethod(name)}
              icons={icons}
              active={paymentMethod === name}
            >
              {title}
            </PaymentOption>
          ))}
        </PaymentMethodOptions>
        {/* <PaymentTaxId /> */}
        <PaymentInfo>
          <Info>{t('paymentInfo')}</Info>
        </PaymentInfo>
      </PaymentMethodWrapper>
    </Step>
  )
}

export default Payment
